/**
 * One lane of the pool — colour rail + letter, the gateway's name, mono
 * routeId, what it sends with, weight (Manual % only) and its SMS-a-day cap,
 * drag grip, remove. The row GROWS on click (Phase 40): its own sender IDs
 * and AI prompt open under it — no inner "Open…" button.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ChevronDown, GripVertical, X } from 'lucide-react';

import { cn } from '@/lib/utils';

import type { DraftPoolRow, DraftSenderDefaults } from '../../../../drop-generator.types';
import type { GatewayOp } from '../../state/gateway-draft';
import { gwColor, gwLetter } from '../cards/strategy-copy';
import { SendsWithCell } from '../senders/SendsWithCell';
import { LaneSenders } from '../senders/LaneSenders';
import { sendsWithFor } from '../senders/sender-words';
import { PoolRowInputs } from './PoolRowInputs';

interface Props {
  countryKey?: string;
  row: DraftPoolRow;
  index: number;
  name: string;
  defaults: DraftSenderDefaults;
  showWeight: boolean;
  onOp: (op: GatewayOp) => void;
}

export const PoolRow: FC<Props> = ({ row, index, name, defaults, showWeight, onOp, countryKey }) => {
  const [open, setOpen] = useState(false);
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id: row.id });
  const letter = gwLetter(index);
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn('flex flex-col rounded-lg border border-border/60 transition-colors', isDragging && 'z-10 bg-background shadow-md', open && 'border-primary/40')}
      data-testid={`pool-row-${row.id}`}
    >
      <div className="flex items-center gap-2.5 py-1.5 pl-0 pr-2">
        <span className="h-8 w-1 shrink-0 rounded-r-sm" style={{ backgroundColor: gwColor(index) }} />
        <button ref={setActivatorNodeRef} type="button" aria-label={`Reorder ${letter} · ${name}`} className="touch-none cursor-grab text-muted-foreground/70 hover:text-foreground" {...attributes} {...listeners}>
          <GripVertical className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          aria-expanded={open}
          aria-label={`Lane ${letter} · ${name} — senders`}
          onClick={() => setOpen((o) => !o)}
          className="flex min-w-0 grow items-center gap-2.5 rounded-md text-left outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
        >
          <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded font-mono text-[11px] font-bold text-white" style={{ backgroundColor: gwColor(index) }}>
            {letter}
          </span>
          <span className="min-w-0 truncate text-sm font-medium">{name}</span>
          <span className="shrink-0 font-mono text-[11px] tabular-nums text-muted-foreground">#{row.routeId}</span>
          <SendsWithCell value={sendsWithFor(defaults, row)} />
          <ChevronDown className={cn('h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform duration-200', open && 'rotate-180')} aria-hidden />
        </button>
        <PoolRowInputs row={row} showWeight={showWeight} onOp={onOp} />
        <button type="button" aria-label={`Remove ${letter} · ${name}`} onClick={() => onOp({ kind: 'poolRemove', id: row.id })} className="rounded-md p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      {open ? <LaneSenders countryKey={countryKey} lane={row} defaults={defaults} onOp={onOp} /> : null}
    </div>
  );
};
